import { map, share } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IrisProjectI, IrisProjectPage, ProjectWithRoles } from '@iris/common/models';
import { ApiUrl, Cacheable, IrisBaseCRUDService, IrisQueryApiClient, IrisQueryParams, IrisQueryParamsBuilder } from '@iris/api-query';
import { TranslateService } from '@ngx-translate/core';
import { IrisMFChecklist } from '../modules/module-filter/models/IrisMFChecklist';
import { IrisMFMultiselect } from '../modules/module-filter/models/IrisMFMultiselect';
import { IrisMFContains } from '../modules/module-filter/models/IrisMFContains';
import { IrisProjectTypeI } from '../models/irisProjectType';
import { IrisMFBetween } from '../modules/module-filter/models/IrisMFBetween';
import { IrisTimePipe } from '@iris/common/modules/time';
import { IrisColorService } from '@iris/common/services/color.service';
import { IrisProjectUserI } from '../models/IrisProjectUser';
import { IrisPage } from '../models/page';
import { CostCenter } from '@iris/modules/projects/common/project-form/models/cost-center';
import { CostCenterSearchingDataI } from '@iris/modules/projects/common/project-form/models/cost-center-searching-data';
import {
  DropboxSyncResult,
} from '@iris/modules/projects/details/settings/components/project-dropbox-integration/project-dropbox-integration.component';
import { AdditionalWarranty, WarrantyProjectTradeI } from '@iris/modules/projects/details/models/warranty-project-trade';
import { StrasyncCompanyDetails } from '@iris/common/models/strasync-company-details';
import { ProjectHistoryI, ProjectHistoryResponseI } from '@iris/modules/system-configuration/system-configuration-list/projects/models/project-history';

@Injectable({
  providedIn: 'root',
})
@ApiUrl('/projects')
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class IrisProjectsService extends IrisBaseCRUDService<any> implements IrisQueryApiClient<IrisProjectI> {
  private readonly translate = inject(TranslateService);
  private readonly irisTimePipe = inject(IrisTimePipe);
  private readonly projectColors = inject(IrisColorService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectsListFilterMeta: any[] = [];

  constructor() {
    const httpClient = inject(HttpClient);

    super(httpClient);

    this.projectsListFilterMeta = [
      new IrisMFContains(
        'name',
        {
          label: this.translate.instant('label.Name'),
          orFields: ['shortName'],
        },
      ),
      new IrisMFMultiselect(
        'countryId',
        {
          formFieldName: 'countryIds',
          directoryFieldName: 'filterCountries',
          label: this.translate.instant('label.Country'),
          fullDescription: true,
        },
      ),
      new IrisMFMultiselect(
        'companyId',
        {
          formFieldName: 'companyIds',
          directoryFieldName: 'filterCompanies',
          label: this.translate.instant('label.Company'),
          fullDescription: true,
        },
      ),
      new IrisMFMultiselect(
        'workflowStateId',
        {
          formFieldName: 'stateIds',
          directoryFieldName: 'filterStates',
          label: this.translate.instant('label.States'),
          fullDescription: true,
        },
      ),
      new IrisMFMultiselect(
        'responsibleUserId',
        {
          formFieldName: 'responsibleUserIds',
          directoryFieldName: 'users',
          valueFieldName: 'fullName',
          label: this.translate.instant('label.ProjectLeader'),
        },
      ),
      new IrisMFMultiselect(
        'contactPersonId',
        {
          formFieldName: 'contactPersonIds',
          directoryFieldName: 'users',
          valueFieldName: 'fullName',
          label: this.translate.instant('label.ContactPerson'),
        },
      ),
      new IrisMFContains('departmentId', { label: this.translate.instant('label.Division') }),
      new IrisMFContains('strathekNumber', { label: this.translate.instant('label.STRAthekNumber') }),
      new IrisMFContains('projectNumber', { label: this.translate.instant('label.ProjectNumber') }),
      new IrisMFMultiselect(
        'projectTypeId',
        {
          formFieldName: 'projectTypes',
          directoryFieldName: 'filterProjectTypes',
          textFieldName: 'label',
          valueFieldName: 'value',
          label: this.translate.instant('label.ProjectTypes'),
          fullDescription: true,
        },
      ),
      new IrisMFContains('shortName', { label: this.translate.instant('label.ShortName') }),
      new IrisMFBetween(
        'from',
        {
          formFieldName: 'from',
          label: this.translate.instant('label.From'),
          stringifyFn: (v) => this.irisTimePipe.transform(v),
        },
      ),
      new IrisMFBetween(
        'to',
        {
          formFieldName: 'to',
          label: this.translate.instant('label.To'),
          stringifyFn: (v) => this.irisTimePipe.transform(v),
        },
      ),
      new IrisMFContains('irisCode', { label: this.translate.instant('label.IrisCode') }),
      new IrisMFContains('ld', { label: this.translate.instant('label.projects.LD') }),
      new IrisMFContains('fco', { label: this.translate.instant('label.projects.FCO') }),
      new IrisMFContains('kost', { label: this.translate.instant('label.projects.KOST') }),
      new IrisMFMultiselect(
        'contractorId',
        {
          formFieldName: 'contractorIds',
          directoryFieldName: 'filterContractors',
          label: this.translate.instant('label.projects.Contractor'),
          fullDescription: true,
        },
      ),
      new IrisMFBetween(
        'orderValue',
        {
          formFieldName: 'orderValue',
          label: this.translate.instant('label.OrderValue'),
        },
      ),
      new IrisMFBetween(
        'cost',
        {
          formFieldName: 'cost',
          label: this.translate.instant('label.projects.TotalBudget'),
        },
      ),
      new IrisMFChecklist(
        'resolution',
        {
          checks: [
            { f: 'open', v: 'null', l: this.translate.instant('label.Open') },
            { f: 'resolved', v: '"RESOLVED"', l: this.translate.instant('label.Resolved') },
            { f: 'rejected', v: '"REJECTED"', l: this.translate.instant('label.Rejected') },
          ],
          label: this.translate.instant('label.Resolution'),
        },
      ),
      new IrisMFChecklist(
        'isActive',
        {
          checks: [
            { f: 'inactive', v: false, l: this.translate.instant('label.NotActive') },
            { f: 'active', v: true, l: this.translate.instant('label.Active') },
          ],
          label: this.translate.instant('label.Active'),
        },
      ),
      new IrisMFChecklist(
        'isArchived',
        {
          checks: [
            { f: 'true', v: true, l: this.translate.instant('label.Archived') },
          ],
          label: this.translate.instant('label.Archived'),
        },
      ),
      new IrisMFMultiselect(
        'escalation',
        {
          formFieldName: 'escalations',
          directoryFieldName: 'allEscalations',
          label: this.translate.instant('label.EscalationLevel'),
          fullDescription: true,
        },
      ),
      new IrisMFMultiselect(
        'progress',
        {
          formFieldName: 'progresses',
          directoryFieldName: 'allProgresses',
          label: this.translate.instant('label.pm.Progress'),
          fullDescription: true,
        },
      ),
      new IrisMFBetween(
        'orderValue',
        {
          formFieldName: 'orderValue',
          label: this.translate.instant('label.OrderValue'),
        }),
      new IrisMFBetween(
        'cost',
        {
          formFieldName: 'cost',
          label: this.translate.instant('label.projects.TotalBudget'),
        }),
      new IrisMFChecklist(
        'priority',
        {
          checks: this.getPriorities().map(p => {
            return { f: p.valueField, v: p.id, l: p.name };
          }),
          label: this.translate.instant('label.Priority'),
        },
      ),
    ];
  }

  public getPriorities() {
    return [
      {
        id: 'LOW',
        valueField: 'low',
        name: this.translate.instant('label.Low'),
        color: this.projectColors.colors['$secondaryGrey400'],
        icon: 'text-muted fa-arrow-down',
      },
      {
        id: 'NORMAL',
        valueField: 'normal',
        name: this.translate.instant('label.Normal'),
        color: this.projectColors.colors['$primaryBlue'],
        icon: 'text-success fa-arrow-right',
      },
      {
        id: 'HIGH',
        valueField: 'high',
        name: this.translate.instant('label.High'),
        color: this.projectColors.colors['$errorDark'],
        icon: 'text-danger fa-arrow-up',
      },
    ];
  }

  public getProjectsListCharts() {
    return [
      { id: 'DEPARTMENT', title: this.translate.instant('label.Divisions') },
      { id: 'COUNTRY', title: this.translate.instant('label.Countries') },
    ];
  }

  @ApiUrl('~')
  public add(project: IrisProjectI, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectI> {
    return this.httpClient.post<IrisProjectI>(this.url(), project, { params: params.toObject() });
  }

  @ApiUrl('~/{id}')
  public saveProject(project: IrisProjectI, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectI> {
    return this.httpClient.post<IrisProjectI>(this.url({ id: project.id }), project, { params: params.toObject() });
  }

  @ApiUrl('~/by-strathek-number/{strathekNumber}')
  getProjectByStrathekNumber(strathekNumber: string, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectI> {
    return this.httpClient.get<IrisProjectI>(this.url({ strathekNumber }), { params: params.toObject() });
  }

  @Cacheable()
  public getProjectById(id: number): Observable<IrisProjectI>  {
    return this.getById(id);
  }

  getProjects(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectI[]> {
    const queryParams = new IrisQueryParamsBuilder(params)
      .filter('archived', [false])
      .toStructure();

    return this.getAll(queryParams).pipe(
      share({
        connector: () => new ReplaySubject(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    );
  }

  @ApiUrl('~/page')
  getProjectsPage(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectPage> {
    const queryParams = new IrisQueryParamsBuilder(params)
      .filter('archived', [false])
      .toObject();

    return this.httpClient.get<IrisProjectPage>(this.url(), { params: queryParams }).pipe(
      share({
        connector: () => new ReplaySubject(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    );
  }
  @ApiUrl('~/get-with-post-filter')
  fetchProjectsByIds(body: Record<string, unknown>, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectPage> {
    return this.httpClient.post<IrisProjectPage>(this.url(), body, { params: params.toObject() });
  }

  @ApiUrl('~/my')
  getMyProjects(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisPage<ProjectWithRoles>> {
    const queryParams = new IrisQueryParamsBuilder(params)
      .filter('archived', [false])
      .toObject();

    return this. httpClient.get<IrisPage<ProjectWithRoles>>(this.url(), { params: queryParams });
  }

  @ApiUrl('~/workflow-states-statistics')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getWorkflowStateStatistics(projectIds: number[]): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.httpClient.post<any>(this.url(), projectIds);
  }

  @ApiUrl('~/charts-data')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChartsData(params: IrisQueryParams = new IrisQueryParams()): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.httpClient.get<any>(
      this.url(),
      {
        params: new IrisQueryParamsBuilder(params)
          .urlParam(
            'chartNames',
            this.getProjectsListCharts().map(({ id }) => id),
          )
          .toObject(),
      },
    );
  }

  @ApiUrl('/project-type')
  getProjectTypes(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectTypeI[]> {
    return this.httpClient.get<IrisProjectTypeI[]>(this.url(), { params: params.toObject() });
  }

  @ApiUrl('/project-type/{id}')
  getProjectTypeById(id: number, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectTypeI> {
    return this.httpClient.get<IrisProjectTypeI>(this.url({ id }), { params: params.toObject() });
  }

  @ApiUrl('/project-type')
  getDefaultProjectType(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisProjectTypeI> {
    params = new IrisQueryParamsBuilder(params)
      .clearLimit().limit(1)
      .clearFilter().filter('isDefault', [true])
      .toStructure();

    return this.httpClient.get<IrisProjectTypeI[]>(this.url(), { params: params.toObject() }).pipe(
      map(types => types.length ? types[0] : null),
    );
  }

  @ApiUrl('/project-type/active/toggle/{id}')
  setActiveProjectType(id: number): Observable<void> {
    return this.httpClient.post<void>(this.url({ id }), {});
  }

  @ApiUrl('/project-type/{id}/change-default/{value}')
  setDefaultProjectType(id: number, value = true): Observable<IrisProjectTypeI> {
    return this.httpClient.post<IrisProjectTypeI>(this.url({ id, value }), {});
  }

  @ApiUrl('/project-type/{id}')
  deleteProjectType(id: number, value = true): Observable<void> {
    return this.httpClient.delete<void>(this.url({ id, value }));
  }

  @ApiUrl('/project-type/{id}')
  saveProjectType(projectType: IrisProjectTypeI): Observable<IrisProjectTypeI> {
    return this.httpClient.post<IrisProjectTypeI>(this.url( { id: projectType.id }), projectType);
  }

  @ApiUrl('~/users/{userId}/project-users')
  public getProjectUsersByUser(userId: number, params = new IrisQueryParams()): Observable<IrisProjectUserI[]> {
    return this.httpClient.get<IrisProjectUserI[]>(this.url({ userId }), { params: params.toObject() });
  }

  @ApiUrl('~/{projectId}/project-users/{projectUserId}')
  public deleteProjectUser(projectId: number, projectUserId: number): Observable<IrisProjectUserI> {
    return this.httpClient.delete<IrisProjectUserI>(this.url({ projectId, projectUserId }));
  }

  @ApiUrl('~/{projectId}/project-users/{userId}')
  public updateProjectUser(projectUser: IrisProjectUserI): Observable<IrisProjectUserI> {
    return this.httpClient.post<IrisProjectUserI>(this.url({ projectId: projectUser.projectId, userId: projectUser.id }), projectUser);
  }

  @ApiUrl('/users/{userId}/current-project-user')
  public getCurrentProjectUserByUserId(userId: number): Observable<IrisProjectUserI> {
    return this.httpClient.get<IrisProjectUserI>(this.url({ userId }));
  }

  public getProjectViewUrl(id: number): string {
    return `/ui/ui/projects-list/project/${id}/view/general`;
  }

  @ApiUrl('~/info')
  public getProjectsPageInfo(params = new IrisQueryParams()): Observable<IrisProjectPage> {
    const queryParams = new IrisQueryParamsBuilder(params)
      .filter('archived', [false])
      .toObject();

    return this.httpClient.get<IrisProjectPage>(this.url(), { params: queryParams });
  }

  @ApiUrl('~/info-with-roles')
  public getProjectsPageInfoWithRoles(params = new IrisQueryParams()): Observable<IrisPage<ProjectWithRoles>> {
    const queryParams = new IrisQueryParamsBuilder(params)
      .filter('archived', [false])
      .toObject();

    return this.httpClient.get<IrisPage<ProjectWithRoles>>(this.url(), { params: queryParams });
  }

  @ApiUrl('~/info/{projectId}')
  @Cacheable()
  public getProjectInfo(projectId: number, params = new IrisQueryParams()): Observable<IrisProjectI> {
    return this.httpClient.get<IrisProjectI>(this.url({ projectId }), { params: params.toObject() });
  }

  public anyProjectAvailable(): Observable<boolean> {
    const params = new IrisQueryParamsBuilder()
      .limit(1)
      .onlyFields(['id'])
      .toStructure();

    return this.httpClient.get<IrisProjectI[]>(this.url(), { params: params.toObject() }).pipe(
      map(project => !!project),
    );
  }

  query(queryParams?: IrisQueryParams): Observable<IrisProjectI[]> {
    return this.getProjects(queryParams);
  }

  @Cacheable()
  @ApiUrl('/strasync/{iris@instanceId}/exchange/company/details')
  public getCompanyDetailsForAProject(project: IrisProjectI): Observable<StrasyncCompanyDetails> {
    return this.httpClient.post<StrasyncCompanyDetails>(this.url(), { ld: project.ld, fco: project.fco });
  }

  @ApiUrl('/strasync/{iris@instanceId}/exchange/hierarchy/validate')
  public validateCostCenter(kostCenter: CostCenter): Observable<CostCenter> {
    return this.httpClient.post<CostCenter>(this.url(), kostCenter);
  }

  @ApiUrl('/strasync/{iris@instanceId}/exchange/search/kost')
  public searchCostCenters(searchingData: CostCenterSearchingDataI): Observable<CostCenter[]> {
    return this.httpClient.post<CostCenter[]>(this.url(), searchingData);
  }

  @ApiUrl('~/{projectId}/dropbox-integration')
  activateDropboxSync(projectId: number): Observable<DropboxSyncResult> {
    return this.httpClient.post<DropboxSyncResult>(this.url({ projectId }), {});
  }

  @ApiUrl('~/{id}/complete')
  setProjectPhaseComplete(id: number, params: Partial<IrisProjectI>): Observable<IrisProjectI> {
    return this.httpClient.post<IrisProjectI>(this.url({ id }), params);
  }

  @ApiUrl('~/{id}/warranty')
  setProjectPhaseWarranty(id: number, params: Partial<IrisProjectI>): Observable<IrisProjectI> {
    return this.httpClient.post<IrisProjectI>(this.url({ id }), params);
  }

  @ApiUrl('~/{id}/archive')
  setProjectPhaseArchive(id: number): Observable<void> {
    return this.httpClient.post<void>(this.url({ id }), {});
  }

  @ApiUrl('~/{projectId}/trades-available')
  isTradesAvailable(projectId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(this.url({ projectId }));
  }

  @ApiUrl('~/{projectId}/trades/page')
  getProjectTradesByProjectIdPage(projectId: number): Observable<IrisPage<WarrantyProjectTradeI>> {
    return this.httpClient.get<IrisPage<WarrantyProjectTradeI>>(this.url({ projectId }));
  }

  @ApiUrl('~/{projectId}/trades')
  createProjectTrade(projectId: number, projectTrade: WarrantyProjectTradeI): Observable<WarrantyProjectTradeI> {
    return this.httpClient.post<WarrantyProjectTradeI>(this.url({ projectId }), projectTrade);
  }

  @ApiUrl('~/{projectId}/trades/{tradeId}')
  updateProjectTrade(projectId: number, tradeId: number, projectTrade: WarrantyProjectTradeI): Observable<WarrantyProjectTradeI> {
    return this.httpClient.put<WarrantyProjectTradeI>(this.url({ projectId, tradeId }), projectTrade);
  }

  @ApiUrl('~/{projectId}/trades/{tradeId}')
  deleteProjectTrade(projectId: number, tradeId: number): Observable<WarrantyProjectTradeI> {
    return this.httpClient.delete<WarrantyProjectTradeI>(this.url({ projectId, tradeId }));
  }

  @ApiUrl('~/{projectId}/trades/{tradeId}/additional-warranties')
  addWarrantyEndToTheTrade(projectId: number, tradeId: number, warranty: AdditionalWarranty): Observable<WarrantyProjectTradeI> {
    return this.httpClient.post<WarrantyProjectTradeI>(this.url({ projectId, tradeId }), warranty);
  }

  @ApiUrl('~/{projectId}/trades/{tradeId}/additional-warranties/{additionalWarrantyId}')
  updateWarrantyEnd(projectId: number, tradeId: number, warranty: AdditionalWarranty): Observable<WarrantyProjectTradeI> {
    return this.httpClient.post<WarrantyProjectTradeI>(this.url({ projectId, tradeId, additionalWarrantyId: warranty.id }), warranty);
  }

  @ApiUrl('~/{projectId}/trades/{tradeId}/additional-warranties/{additionalWarrantyId}')
  deleteWarrantyEnd(projectId: number, tradeId: number, additionalWarrantyId: number): Observable<WarrantyProjectTradeI> {
    return this.httpClient.delete<WarrantyProjectTradeI>(this.url({ projectId, tradeId, additionalWarrantyId }));
  }

  @ApiUrl('~/{projectId}/history')
  getProjectHistory(projectId: number): Observable<ProjectHistoryI[]> {
    return this.httpClient.get<ProjectHistoryResponseI>(this.url({ projectId })).pipe(
      map(response => response.projectHistory),
    );
  }
}
